/**
 * guestPortalV2Selectors — lo que el portal del huésped sabe de su estancia.
 *
 * TODO se deriva. Este módulo no declara ni un dato: compone lo que ya existe
 * en el modelo de huésped (`guestsV2Selectors`), en la reserva
 * (`pmsRuntimeStore`) y en Ajustes (`settingsV2Store`). Si algo aquí tuviera
 * que declarar un email, una hora de llegada o un estado de pre-check-in,
 * estaría mal: sería la segunda copia de un dato que ya tiene dueño.
 *
 * Reglas que se siguen aquí:
 *   1. El huésped ve SU reserva y nada más. El token resuelve a una y solo una.
 *   2. Una sola acción siguiente. No una lista de tareas: una.
 *   3. Inmigración NO se le enseña como algo que él pueda hacer (§7.4). Es
 *      trabajo de la propiedad; el huésped no tiene ninguna acción ahí.
 *   4. Menos privilegio por fase (§7.3.2): del documento y de los datos de los
 *      demás huéspedes, el portal es de ESCRITURA. No se los devuelve.
 *   5. Nada operativo interno: ni asignación, ni dinero, ni notas del equipo.
 *
 * Sin estado propio, sin efectos, sin red.
 */
import { DEMO_TODAY_ISO } from "@/data/pmsDemoData";
import { nightsBetween } from "@/lib/pmsSelectors";
import { buildGuestParty, reservationIdForToken, asksTransfer } from "@/lib/guestsV2Selectors";
import { segmentOf, unitTypeFor } from "@/lib/reservationsV2Selectors";
import { arrivalModeOf } from "@/data/guestsV2Demo";
import { STAY_PHASE_LABEL, GUEST_TRANSFER_LABEL } from "@/data/guestPortalV2Demo";

// ---------------------------------------------------------------------------
// En qué momento de la estancia está
// ---------------------------------------------------------------------------

/** `segmentOf` habla en operativo; el portal habla en huésped. */
const PHASE_BY_SEGMENT = {
  upcoming: "before",
  arrival: "arrival_day",
  in_house: "in_house",
  departure: "departure_day",
  past: "completed",
  cancelled: "cancelled",
};

/**
 * La fase se deriva del calendario, igual que en Reservas. No se mezcla con
 * `stay_status`: son ejes distintos (§6.2) y fundirlos aquí crearía justo la
 * inferencia cruzada que el modelo evita en todas partes.
 */
export function stayPhaseOf(reservation, today = DEMO_TODAY_ISO) {
  return PHASE_BY_SEGMENT[segmentOf(reservation, today)] || "before";
}

// ---------------------------------------------------------------------------
// La única acción siguiente
// ---------------------------------------------------------------------------

/**
 * Qué tiene que hacer el huésped AHORA, en una frase y como mucho un botón.
 *
 * Se deriva de lo que ya se sabe: lo que falta del pre-check-in y en qué punto
 * de la estancia está. No hay ningún interruptor manual detrás, ni una lista de
 * pendientes: si hubiera que marcar a mano "este huésped ya está listo",
 * volveríamos a tener dos verdades sobre el mismo hecho.
 *
 * Lo que NUNCA sale de aquí es una acción de inmigración. El reporte de
 * extranjeros lo presenta la propiedad (§7.4); ofrecérselo al huésped sería
 * pedirle que haga un trámite que no le corresponde y que no puede hacer.
 */
export function nextActionFor(view, phase) {
  const missing = view.missing.length;

  if (phase === "cancelled") {
    return { key: "cancelled", title: "Esta reserva está cancelada", cta: null, tone: "stone" };
  }
  if (phase === "completed") {
    return { key: "completed", title: "Gracias por tu estancia", cta: null, tone: "stone" };
  }
  if (phase === "departure_day") {
    return { key: "departure", title: "Hoy te vas", cta: null, tone: "stone" };
  }
  if (phase === "in_house") {
    return { key: "in_house", title: "Tu estancia está en curso", cta: null, tone: "emerald" };
  }

  // Antes de llegar: lo único que puede hacer es completar sus datos.
  if (view.pre_checkin === "not_started") {
    return {
      key: "start_precheckin",
      title: "Completar pre-check-in",
      detail: "Nos hace falta antes de que llegues.",
      cta: { label: "Empezar", area: "checkin" },
      tone: "amber",
    };
  }
  if (missing > 0) {
    return {
      key: "finish_precheckin",
      title: missing === 1 ? "Te falta 1 dato" : `Te faltan ${missing} datos`,
      detail: "Un momento y lo tienes.",
      cta: { label: "Continuar", area: "checkin" },
      tone: "amber",
    };
  }
  return {
    key: "ready",
    title: "Todo listo para tu llegada",
    detail: null,
    // Puede repasarlo, no se le obliga a repetirlo.
    cta: { label: "Revisar mis datos", area: "checkin" },
    tone: "emerald",
  };
}

// ---------------------------------------------------------------------------
// Llegada, en el idioma del huésped
// ---------------------------------------------------------------------------

/**
 * Resumen de la llegada, derivado del ÚNICO modelo de llegada que existe: el
 * del huésped. Deliberadamente NO se lee `stayFor().eta` ni
 * `stayFor().transfer`, que salen de `todayV2Demo.ARRIVAL_DETAILS` y son una
 * segunda declaración de los mismos hechos.
 *
 * Si el huésped llega por su cuenta no se le habla de aeropuertos ni de
 * puertos: no hay punto de recogida y no se le ofreció ningún traslado.
 */
export function arrivalSummaryOf(view) {
  const arrival = view.arrival;
  if (!arrival) return null;
  const mode = arrivalModeOf(arrival.mode);
  return {
    mode: arrival.mode,
    mode_label: mode.label,
    // La referencia solo se enseña con la etiqueta del modo que la pidió.
    reference: mode.ref_label && arrival.reference ? arrival.reference : null,
    reference_label: mode.ref_label,
    landing_time: mode.time_label ? arrival.landing_time || null : null,
    landing_label: mode.time_label,
    estimated_arrival: arrival.estimated_arrival || null,
    // Si no se le preguntó por el traslado, no se le contesta: quien llega
    // por su cuenta no tiene punto de recogida.
    transfer: asksTransfer(arrival)
      ? GUEST_TRANSFER_LABEL[arrival.transfer || "none"] || null
      : null,
  };
}

// ---------------------------------------------------------------------------
// La vista del portal
// ---------------------------------------------------------------------------

/**
 * Todo lo que el portal necesita para una reserva, y nada que el huésped no
 * deba ver.
 *
 * Lo que NO se devuelve, a propósito: dinero, canal, comisiones, puntuación de
 * asignación, notas del equipo, estado del documento de nadie, quién presentó
 * el reporte de extranjeros, ni los datos personales de los acompañantes. El
 * portal recoge esos datos (§7.2) pero no los devuelve (§7.3.2), así que aquí
 * solo viaja CUÁNTOS son.
 */
export function buildGuestPortalView({ reservation, settings, guestState, today = DEMO_TODAY_ISO }) {
  const party = buildGuestParty({ reservation, settings, storeState: guestState });
  const phase = stayPhaseOf(reservation, today);
  const unitType = unitTypeFor(reservation, settings);

  return {
    reservation_id: reservation.reservation_id,
    phase,
    phase_label: STAY_PHASE_LABEL[phase],
    property: settings.property,

    // Quién eres tú. De los demás, solo cuántos sois.
    guest_name: party.primary?.full_name || reservation.guest?.name || null,
    party_size: party.party.expected || party.party.declared,

    // Las fechas salen de la reserva y los horarios de Ajustes, sin pasar por
    // `stayFor()`: ése añade `eta` y `transfer` desde `todayV2Demo`, que es la
    // otra declaración de la llegada. El portal no la toca ni de rebote.
    stay: {
      check_in: reservation.check_in,
      check_out: reservation.check_out,
      nights: nightsBetween(reservation.check_in, reservation.check_out),
      check_in_time: settings.property.check_in_time,
      check_out_time: settings.property.check_out_time,
    },

    // Solo el TIPO de alojamiento, que es lo que el huésped contrató (§2.2:
    // el tipo es "the commercial inventory category the guest books"; la
    // unidad, "the physical item that is reserved, cleaned and owned").
    //
    // La unidad física NO viaja al portal, esté asignada o no.
    // `assigned_villa_id != null` NO significa que la asignación sea
    // definitiva ni que pueda enseñarse:
    //
    //   - la única regla de exposición al huésped que existe es una lista
    //     blanca y la unidad no está en ella: "before arrival the link exposes
    //     ONLY the check-in flow and upsells" (§7.3.2);
    //   - el reparto manual se puede forzar en cualquier momento, sin más
    //     requisito que un motivo (§6.4);
    //   - una incidencia crea un bloqueo sobre una unidad ya asignada (§8.2) y
    //     el propio texto da por hecho que la reserva se mueve a otra ("never
    //     while a reservation was already moved elsewhere");
    //   - la unidad es un *operational change* que "live[s] only in Dolce"
    //     (§13.3): un campo interno, no un compromiso comercial con el huésped.
    //
    // No hay ninguna regla aprobada sobre cuándo una asignación es estable.
    // Tampoco está en la lista de decisiones aplazadas: sencillamente no se ha
    // decidido. Prometer una villa concreta que luego cambia es peor que no
    // nombrarla, así que aquí no se nombra. Cuando la regla se apruebe —y se
    // escriba en PRODUCT_DECISIONS.md— este es el sitio donde añadir el campo.
    unit_type: unitType ? unitType.name : null,

    pre_checkin: party.pre_checkin,
    missing_count: party.missing.length,
    arrival: arrivalSummaryOf(party),
    next_action: nextActionFor(party, phase),

    // El contacto que consta, para que pueda corregirlo si no es suyo.
    contact: { email: party.contact.email, phone: party.contact.phone },
  };
}

/** El portal de un token, o `null` si el token no resuelve a nada. */
export function portalViewForToken({ token, reservations, settings, guestState, today = DEMO_TODAY_ISO }) {
  const id = reservationIdForToken(token);
  if (!id) return null;
  const reservation = (reservations || []).find((r) => r.reservation_id === id);
  if (!reservation) return null;
  return buildGuestPortalView({ reservation, settings, guestState, today });
}

// ---------------------------------------------------------------------------
// Formato
// ---------------------------------------------------------------------------

const MONTHS = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];

const dayOf = (iso) => Number(String(iso).slice(8, 10));
const monthOf = (iso) => MONTHS[Number(String(iso).slice(5, 7)) - 1];

/**
 * "21–25 agosto · 4 noches", y "29 agosto – 2 septiembre" cuando cruza de mes.
 * Sin año: el huésped sabe en qué año viaja.
 */
export function stayDatesLabel(stay) {
  if (!stay?.check_in || !stay?.check_out) return "";
  const from = stay.check_in;
  const to = stay.check_out;
  const range = monthOf(from) === monthOf(to)
    ? `${dayOf(from)}–${dayOf(to)} ${monthOf(to)}`
    : `${dayOf(from)} ${monthOf(from)} – ${dayOf(to)} ${monthOf(to)}`;
  const nights = stay.nights === 1 ? "1 noche" : `${stay.nights} noches`;
  return `${range} · ${nights}`;
}

/** "21 agosto". Al huésped no se le enseña una fecha ISO en su propia pantalla. */
export function dayLabel(iso) {
  if (!iso) return "";
  return `${dayOf(iso)} ${monthOf(iso)}`;
}

export function partyLabel(size) {
  return size === 1 ? "1 huésped" : `${size} huéspedes`;
}
